/** The Permit workspace's draft and the buy-and-return flow (pure; tested in permitDraft.test.ts).
 *
 * The draft (`GET`/`PUT /v1/designs/{id}/permit-draft`) keeps on the server what the installer chose that is not a
 * site detail or an SLD edit: the plan-set request inputs and the step they were on. With the site details, the
 * photos and the SLD edits (their own routes) it is everything the workspace needs to come back exactly as it was
 * after leaving: to buy Lux (Stripe Checkout returns to `/designs/<id>/permit?step=review&checkout=paid`), a reload,
 * another device. */
import { cleanInputs, type Country, type Tr } from "./permits.ts";
import { isStep, type StepId } from "./permitWorkspace.ts";

export { buyLuxHref, checkoutResult, checkoutReturnUrls, safeReturnPath } from "./luxSpend.ts";

/** The request inputs as the workspace's forms hold them (the licence flat). */
export type Inputs = {
  country?: Country;
  module_id?: string;
  inverter_id?: string;
  optimizer_id?: string;
  utility?: string;
  ahj?: string;
  /** The AHJ the installer confirmed (permit_ahjs); the name goes with it in `ahj`. */
  ahj_id?: string;
  design_low_c?: number | null;
  design_high_c?: number | null;
  licence?: string;
};

/** api PlanSetInputs, as far as the workspace sets it. */
export type RequestInputs = Omit<Inputs, "licence"> & { contractor?: { licence?: string | null } };

/** What the preview, the generation and the draft send. */
export function requestBody(inputs: Inputs): RequestInputs {
  const { licence, ...rest } = inputs;
  const clean = cleanInputs(rest) as RequestInputs;
  return licence ? { ...clean, contractor: { licence } } : clean;
}

/** The forms' inputs from a saved draft (the API answers every field, null when unset). */
export function inputsOf(body: Record<string, unknown> | null | undefined): Inputs {
  if (!body) return {};
  const { contractor, ...rest } = body as { contractor?: { licence?: string | null } | null } & Record<string, unknown>;
  const out = cleanInputs(rest as Record<string, unknown>) as Inputs;
  const keep: (keyof Inputs)[] = ["country", "module_id", "inverter_id", "optimizer_id", "utility", "ahj", "ahj_id", "design_low_c", "design_high_c"];
  const picked: Inputs = {};
  for (const k of keep) if (k in out) (picked as Record<string, unknown>)[k] = (out as Record<string, unknown>)[k];
  const licence = contractor?.licence;
  return licence ? { ...picked, licence } : picked;
}

/** The draft the workspace autosaves (inputs and step). */
export function draftBody(inputs: Inputs, step: StepId): { inputs: RequestInputs; step: StepId } {
  return { inputs: requestBody(inputs), step };
}

/** Where the workspace opens: the URL's step (a link, the checkout return), else the saved one, else Equipment. */
export function initialStep(urlStep: string | null | undefined, draftStep: string | null | undefined): StepId {
  if (isStep(urlStep)) return urlStep;
  if (isStep(draftStep)) return draftStep;
  return "equipment";
}

/** The workspace at a step: where "Buy Lux" comes back to. */
export function permitPath(designId: string, step: StepId = "review"): string {
  return `/designs/${designId}/permit?step=${step}`;
}

export interface PurchasePrompt {
  /** "Adding your Lux…" until the payment is confirmed (a webhook; usually seconds). */
  waiting: boolean;
  title: string;
  detail: string;
  /** Whether the company can now pay the plan set. */
  canGenerate: boolean;
}

/** The prompt after a purchase, on the Review step: "You now have N Lux, generate the plan set?" */
export function purchasePrompt(t: Tr, spendable: number | null | undefined, cost: number, arrived: boolean): PurchasePrompt {
  if (!arrived || spendable == null)
    return { waiting: true, title: t("permit.purchase.received"), detail: t("permit.purchase.adding"), canGenerate: false };
  const canGenerate = cost <= 0 || spendable >= cost;
  return {
    waiting: false,
    title: t("permit.purchase.balance", { lux: spendable / 100 }),
    detail: canGenerate
      ? cost > 0
        ? t("permit.purchase.generateCost", { lux: cost / 100 })
        : t("permit.purchase.generateFree")
      : t("permit.purchase.notEnough", { lux: cost / 100 }),
    canGenerate,
  };
}

/** Whether the purchase has reached the wallet: the spendable Lux grew since the page opened (or now pays). */
export function purchaseArrived(before: number | null | undefined, now: number | null | undefined, cost: number): boolean {
  if (now == null) return false;
  if (before == null) return now >= cost;
  return now > before || now >= cost;
}
